import type {
	BackfillStream,
	Integration,
	IntegrationBackfill,
	IntegrationCredential,
	IntegrationInterval,
	IntegrationProvider,
	IntegrationProviderOption,
	IntegrationRunResult,
	IntegrationStatus,
	IntegrationType,
} from '@app/model/integration-settings/Integration';

// Text and rules of the Integration Settings screen. Only type imports here: the unit tests run this file alone.

export const TYPE_LABEL: Record<IntegrationType, string> = {
	AVAILABILITY: 'Availability',
	RESERVATIONS: 'Reservations',
	PRICING: 'Pricing',
};

export const TYPE_TEXT: Record<IntegrationType, string> = {
	AVAILABILITY: 'RoomPulse reads the rooms that the property has free.',
	RESERVATIONS: 'RoomPulse reads the bookings of the property.',
	PRICING: 'RoomPulse sends prices to the system of the property.',
};

export const TYPE_ORDER: IntegrationType[] = ['AVAILABILITY', 'RESERVATIONS', 'PRICING'];

export const PROVIDER_LABEL: Record<IntegrationProvider, string> = {
	WEBHOTELIER: 'WebHotelier',
	FIVE_STELLE: '5stelle',
	HOTELIZER: 'Hotelizer',
	OPERA: 'Opera',
};

// The providers of a process come from the API (ROO2-296). The unavailable ones show as "coming soon" in a help line.
export const providerOption = (integration: Integration, provider: string): IntegrationProviderOption | undefined =>
	integration.providers.find(option => option.provider === provider && option.available);

// The help line under the provider select: the planned providers of the process, or none once every provider is
// available (ROO2-316: Hotelizer was the last planned Reservations provider).
export const providerHelp = (integration: Integration, locked: boolean): string | null => {
	if (locked) return 'A saved connection keeps its provider.';
	const planned = integration.providers.filter(provider => !provider.available).map(provider => provider.label);
	return planned.length === 0 ? null : planned.join(' and ') + ': coming soon.';
};

// A structured credential has named fields; the other providers take one value.
export const isStructured = (option: IntegrationProviderOption | undefined): boolean => (option?.credentialForm?.fields.length ?? 0) > 0;

// The saved credential the card shows: the connection's own, or the company's when a new connection is set to a
// shared provider whose credential the company already holds. Another provider's credential is never shown.
export const savedCredential = (integration: Integration, provider: string, option: IntegrationProviderOption | undefined):
	IntegrationCredential | null => {
	if (integration.provider === provider) return integration.credential;
	return option?.credentialForm?.shared === true && option.credentialForm.saved
		? { saved: true, savedAt: null, savedBy: null, shared: true } : null;
};

// The entered fields of a structured credential. Null when none was entered, so the saved credential stays.
export const credentialFieldsInput = (option: IntegrationProviderOption | undefined, values: Record<string, string> | undefined):
	{ name: string, value: string }[] | null => {
	if (option?.credentialForm == null || !isStructured(option)) return null;
	const entered = option.credentialForm.fields.map(field => ({ name: field.name, value: (values?.[field.name] ?? '').trim() }))
		.filter(field => field.value !== '');
	return entered.length === 0 ? null : entered;
};

// The Reservations lines that depend on the provider.
export const RESERVATIONS_SETUP_TEXT: Partial<Record<IntegrationProvider, string>> = {
	FIVE_STELLE: 'Each request covers at most 3 calendar months. Stays and cancellations share 3 calls in any 55 minutes. Scheduled change checks start after the initial backfill.',
	OPERA: 'Each part covers 3 calendar months, and one click imports up to 16 parts. Scheduled change checks start after the initial backfill. Every Sunday at 04:00, the time of the property, RoomPulse reloads the stays from today to the months ahead.',
	HOTELIZER: 'Each part covers the arrivals of 3 calendar months, and one click imports up to 16 parts. Scheduled change checks start after the initial backfill. Every Sunday at 04:00, the time of the property, RoomPulse reloads the stays from today to the months ahead, so that a booked room deleted in Hotelizer leaves the reports.',
};
export const TEST_CONNECTION_TEXT: Partial<Record<IntegrationProvider, string>> = {
	FIVE_STELLE: 'Test connection reads the room types of the property. It uses the room-types daily allowance, separate from the reservation-data allowance.',
	OPERA: 'Test connection signs in to OPERA with the company credential and reads the room types of the property.',
	HOTELIZER: 'Test connection signs in to Hotelizer with the username and password and checks that they belong to the property with this hotel code (the Hotelizer property id).',
};

// Decision 7 and 14: the same four choices for Availability and Reservations.
export const INTERVAL_LABEL: Record<IntegrationInterval, string> = {
	HOURLY: 'Every hour',
	EVERY_6_HOURS: 'Every 6 hours',
	EVERY_12_HOURS: 'Every 12 hours',
	DAILY: 'Every day',
	CUSTOM: 'Custom (set by RoomPulse)',
};
export const INTERVAL_CHOICES: IntegrationInterval[] = ['HOURLY', 'EVERY_6_HOURS', 'EVERY_12_HOURS', 'DAILY'];
export const START_AT_CHOICES: string[] = Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`);
export const MONTHS_AHEAD_CHOICES: Record<IntegrationType, number[]> = {
	AVAILABILITY: [3, 6, 12],
	RESERVATIONS: [6, 12],
	PRICING: [],
};
export const HISTORY_MONTHS_CHOICES: number[] = [3, 6, 12, 24, 36];

export type TagStateName = 'success' | 'warning' | 'critical' | 'info' | 'none';

export const STATUS_TAG: Record<IntegrationStatus, { label: string, state: TagStateName }> = {
	NOT_SET_UP: { label: 'Not set up', state: 'none' },
	WAITING: { label: 'Waiting for first run', state: 'info' },
	ACTIVE: { label: 'Active', state: 'success' },
	LAST_RUN_FAILED: { label: 'Last run failed', state: 'critical' },
	OFF: { label: 'Off', state: 'none' },
};

export const RESULT_TAG: Record<IntegrationRunResult, { label: string, state: TagStateName }> = {
	SUCCESS: { label: 'Success', state: 'success' },
	NO_NEW_DATA: { label: 'No new data', state: 'success' },
	PARTLY_FAILED: { label: 'Partly failed', state: 'warning' },
	FAILED: { label: 'Failed', state: 'critical' },
	WAITING: { label: 'Waiting', state: 'none' },
};

// The records word of a run, per type.
export const RECORDS_WORD: Record<IntegrationType, string> = {
	AVAILABILITY: 'nights read',
	RESERVATIONS: 'stays changed',
	PRICING: 'prices',
};

// API times are in the hotel's time zone, ISO without an offset: "2026-10-06T09:00:00".
export const formatDayTime = (iso: string | null): string => {
	if (iso === null || iso.length < 16) return '—';
	return `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}`;
};

export const formatDate = (isoDate: string): string => `${isoDate.slice(8, 10)}/${isoDate.slice(5, 7)}/${isoDate.slice(0, 4)}`;

export const formatDuration = (seconds: number | null): string => {
    if (seconds === null || !Number.isFinite(seconds)) return '—';
    const whole = Math.max(0, Math.round(seconds));
    if (whole < 60) return `${whole} s`;
    const minutes = Math.floor(whole / 60);
    return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
};

export const findIntegration = (integrations: Integration[], type: IntegrationType): Integration | undefined =>
	integrations.find(integration => integration.type === type);

// The backfill (decisions 20-24, FINAL design): each stream imports its next parts, up to its parts per click.
export const STREAM_LABEL: Record<BackfillStream['kind'], string> = {
	STAYS: 'Stays',
	CANCELLATIONS: 'Cancellations',
};

// The word before "parts" in a sentence: "stays parts 1–3", "cancellation parts 1–2" (the frozen canvas).
const PART_WORD: Record<BackfillStream['kind'], string> = {
	STAYS: 'stays',
	CANCELLATIONS: 'cancellation',
};

export type NextClick = { first: number, last: number, from: string, to: string, numbers?: number[] } | null;

export const nextClick = (stream: BackfillStream): NextClick => {
	const parts = stream.parts.filter(part => part.state !== 'DONE').slice(0, stream.partsPerClick);
	if (parts.length === 0) return null;
	const first = parts[0].number;
	const last = parts[parts.length - 1].number;
	const numbers = parts.map(part => part.number);
	return { first, last, from: parts.map(part => part.from).sort()[0], to: parts.map(part => part.to).sort()[parts.length - 1],
		...(numbers.every((number, index) => number === first + index) ? {} : { numbers }) };

};

export const isComplete = (backfill: IntegrationBackfill): boolean =>
	// An empty plan is not evidence that imports finished.
	backfill.streams.length > 0 && backfill.streams.every(stream =>
		stream.parts.length > 0 && stream.doneParts >= stream.parts.length);

// The API supplies active import leases and quota retry times separately; the browser never compares clocks.
export const isLocked = (backfill: IntegrationBackfill): boolean => backfill.lockedUntil != null || backfill.runningUntil != null;

export const backfillButtonLabel = (backfill: IntegrationBackfill): string => isComplete(backfill) ? 'Backfill complete' : 'Run backfill';

// The API supplies the operation claim or quota retry time; the browser does not compare clocks.
export const backfillStateLine = (backfill: IntegrationBackfill, providerLabel: string): string => {
	if (isComplete(backfill)) {
		const last = backfill.clicks[0];
		const parts = backfill.streams.map(stream => `${stream.parts.length} ${PART_WORD[stream.kind]} parts`).join(' and ');
		return last === undefined ? `All ${parts} imported.` : `All ${parts} imported on ${formatDayTime(last.clickedAt)}.`;
	}
	if (backfill.runningUntil != null) {
		return `A reservation import is already running. Run backfill becomes available when it finishes or the current lock expires at ${formatDayTime(backfill.runningUntil)}, provided calls remain. The lock may be extended while the import runs. This page updates automatically.`;
	}
	if (backfill.lockedUntil !== null) {
		return `Waiting until ${formatDayTime(backfill.lockedUntil)}. ${providerLabel} allows ${backfill.callsPerHour} shared stays and cancellation calls in any 55 minutes.`;
	}
	return backfill.streams.every(stream => stream.doneParts === 0) ? 'Ready. Nothing imported yet.' : 'Ready.';
};

const rangeText = (stream: BackfillStream, next: NonNullable<NextClick>): string => {
	const name = PART_WORD[stream.kind];
	const parts = next.numbers !== undefined ? `${name} parts ${next.numbers.join(', ')}`
		: next.first === next.last ? `${name} part ${next.first}` : `${name} parts ${next.first}–${next.last}`;
	return `${parts} (${formatDate(next.from)} – ${formatDate(next.to)})`;
};

export const nextClickLine = (backfill: IntegrationBackfill): string | null => {
	let remaining = backfill.callsRemaining;
	const ranges = backfill.streams.flatMap(stream => {
		const next = nextClick({ ...stream, partsPerClick: Math.min(stream.partsPerClick, remaining) });
		if (next !== null) remaining -= next.numbers?.length ?? Math.abs(next.last - next.first) + 1;
		return next === null ? [] : [rangeText(stream, next)];
	});
	return ranges.length === 0 ? null : `The next click imports ${ranges.join(' and ')}.`;
};

// The header includes both streams so finished stays cannot imply finished cancellation history.
export const backfillChipText = (backfill: IntegrationBackfill): string | null => {
	if (isComplete(backfill)) return null;
	const done = backfill.streams.reduce((total, stream) => total + stream.doneParts, 0);
	const total = backfill.streams.reduce((sum, stream) => sum + stream.parts.length, 0);
	return `Backfill ${done} of ${total}`;
};
